<template>
    <v-list nav dense>
        <v-list-item v-if="currentOrgOid" active-class="primary--text" :to="backNavigationRoute">
            <v-list-item-icon>
                <v-tooltip right :disabled="!collapsed">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">mdi-arrow-left</v-icon>
                    </template>
                    <span>Back</span>
                </v-tooltip>
            </v-list-item-icon>
            <v-list-item-content>
                <v-list-item-title>Back</v-list-item-title>
            </v-list-item-content>
        </v-list-item>

        <v-subheader class="pl-0">
            <v-hover v-slot:default="{ hover }">
                <v-btn small text color="grey darken-1" class="text-caption" @click="$emit('update:expanded', !expanded)">
                    USER SETTINGS
                    <v-icon v-if="hover" right small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-hover>
        </v-subheader>
        <v-expand-transition>
            <div v-show="expanded">
                <v-list-item active-class="primary--text" :to="{ name: 'user-profile' }" e2e-user-profile>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-account-outline</v-icon>
                            </template>
                            <span>Profile</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-ssh' }" e2e-user-ssh>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-key-outline</v-icon>
                            </template>
                            <span>SSH keys</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>SSH keys</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-secrets' }" e2e-user-secrets>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-lock-outline</v-icon>
                            </template>
                            <span>Secrets</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Secrets</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="!collapsed && currentOrgOid" class="my-0">
                        <SecretCreateDialog :level="0" :entity-id="currentOrgOid">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                    <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                            </template>
                        </SecretCreateDialog>
                    </v-list-item-action>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-env-vars' }" e2e-user-env-vars>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-file-code-outline</v-icon>
                            </template>
                            <span>Variables</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Variables</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="!collapsed && currentOrgOid" class="my-0">
                        <EnvVarCreateDialog :level="0" :entity-id="currentOrgOid">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                    <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                            </template>
                        </EnvVarCreateDialog>
                    </v-list-item-action>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-tables' }" e2e-user-tables>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-database-outline</v-icon>
                            </template>
                            <span>Table access</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Table access</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-dropbox' }" e2e-user-dropbox>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-sync</v-icon>
                            </template>
                            <span>Dropbox sync</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Dropbox sync</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-api-access' }" e2e-user-api-access>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-api</v-icon>
                            </template>
                            <span>API access</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>API access</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-list-item active-class="primary--text" :to="{ name: 'user-nuvolos-settings' }" e2e-nuvolos-settings>
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!collapsed">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-cog-outline</v-icon>
                            </template>
                            <span>Nuvolos settings</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Nuvolos settings</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </div>
        </v-expand-transition>
    </v-list>
</template>

<script>
import { mapState } from 'vuex'

const SecretCreateDialog = () => import('@/components/SecretCreateDialog.vue')
const EnvVarCreateDialog = () => import('@/components/EnvVarCreateDialog.vue')

export default {
    name: 'DrawerUserSettingsSection',
    components: {
        SecretCreateDialog,
        EnvVarCreateDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        expanded: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapState(['userOrgs', 'currentOrgData']),
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapState('spaceStore', ['spaceInstances']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        backNavigationRoute() {
            const oid = this.currentOrgOid
            const sid = this.$route.params.sid

            if (sid) {
                const selectedSpace = this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === sid.toString())
                const iid = this.$route.params.iid || selectedSpace?.iid
                const selectedInstance = iid ? this.spaceInstances.find(instance => instance.iid.toString() === iid.toString()) : null
                const snid = this.$route.params.snid || selectedInstance?.dev_snid || selectedSpace?.snid

                if (oid && iid && snid) {
                    return {
                        name: 'snapshot-overview',
                        params: { oid, sid, iid, snid }
                    }
                }

                if (oid) {
                    return {
                        name: 'home-spaces',
                        params: { oid }
                    }
                }
            }

            return {
                name: 'home-dashboard',
                params: { oid }
            }
        }
    }
}
</script>
